import i18n from '@/i18n';

/**
 * Money is numeric(14,2) EGP and arrives from PostgREST as a string, deliberately —
 * parsing it into a JS number would lose precision above 2^53 and, worse, do it
 * silently. Format from the string; never do arithmetic on it in the client.
 */
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';

  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);

  return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-EG', {
    style: 'currency',
    currency: 'EGP',
    maximumFractionDigits: 0,
    // Western digits even in Arabic: staff read these against Excel exports and
    // bank statements, where ١٢٣ would be unreadable.
    numberingSystem: 'latn',
  }).format(n);
}

/** A compact budget range for list rows. */
export function formatBudget(min: string | null, max: string | null): string {
  if (!min && !max) return '—';
  if (min && max) return `${formatMoney(min)} – ${formatMoney(max)}`;
  return formatMoney(min ?? max);
}

const dateTime = () =>
  new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
    numberingSystem: 'latn',
    timeZone: 'Africa/Cairo',
  });

/** Business time is Africa/Cairo (CLAUDE.md rule 7), not the device's timezone. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return dateTime().format(new Date(iso));
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar-EG' : 'en-GB', {
    dateStyle: 'medium',
    numberingSystem: 'latn',
    timeZone: 'Africa/Cairo',
  }).format(new Date(iso));
}

/** "3 hours ago" / "in 5 minutes", for SLA countdowns and timeline entries. */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';

  const diffMs = new Date(iso).getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(i18n.language === 'ar' ? 'ar-EG' : 'en', {
    numeric: 'auto',
  });

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000_000],
    ['month', 2_592_000_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  for (const [unit, ms] of units) {
    if (Math.abs(diffMs) >= ms) return rtf.format(Math.round(diffMs / ms), unit);
  }
  return rtf.format(Math.round(diffMs / 1000), 'second');
}
